import axios from 'axios';
import { actionsToMutations } from '../utils';
import { normalizeServiceList, OrderCreateService } from '@gid/api-client';
import _ from 'lodash';

import { RequiredByRefCount } from '../ref-counter';
import {
  SERVICE_DEFINITIONS_FETCH,
  SERVICE_SELECTED_ADD,
  SERVICE_SELECTED_ADD_QUANTITY,
  SERVICE_SELECTED_ITEM_DELETE,
  SERVICE_SELECTED_JOB_ITEMS_ADD,
  SERVICE_SELECTED_REFRESH,
  SERVICE_SELECTED_SPECIFY,
  SERVICE_SELECTED_ITEM_UNREF,
  CUSTOM_SERVICE_PUSH_ERROR,
  CUSTOM_SERVICE_RESET_ERRORS,
  SERVICE_PRESELECT_AUTOMATION,
  SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT,
  SERVICE_CHANGE_QUANTITY,
} from './types';

const changeServiceOfServiceGroupAndAdjustQuantity = (
  serviceDefinitions,
  currentActiveService,
  currentActiveServices,
  newService,
  locale,
) => {
  const serviceDefinition = serviceDefinitions[newService.service];
  if (serviceDefinition) {
    const groupName = serviceDefinition.group_name[locale];
    if (groupName) {
      const servicesGroupIds = Object.keys(serviceDefinitions).filter(
        (serviceSfid) =>
          serviceDefinitions[serviceSfid].group_name[locale] == groupName,
      );
      const servicesGroup = servicesGroupIds.map((serviceGroupSfid) => {
        return serviceDefinitions[serviceGroupSfid];
      });

      if (currentActiveService.service == null) {
        // For initial set up
        const selectedServicesIds = currentActiveServices.map((service) => {
          return service.service;
        });
        const matchingServiceDef = servicesGroup.find((serviceGroupDef) => {
          _.intersection(
            selectedServicesIds,
            serviceGroupDef.only_in_combination_with.map((i) => i.service),
          ).length > 0;
        });
        if (matchingServiceDef) {
          newService.service = matchingServiceDef.sfid;
        }
      }
      servicesGroup.sort((a, b) => a.minimum_quantity - b.minimum_quantity);

      for (let i = servicesGroup.length - 1; i > -1; i--) {
        const serviceGroupDef = servicesGroup[i];
        const compareValue = ['one', 'many'].includes(
          serviceGroupDef.one_or_more,
        )
          ? newService.value_entry
          : newService.quantity;
        if (compareValue >= serviceGroupDef.minimum_quantity) {
          newService.service = serviceGroupDef.sfid;
          break;
        }
      }
    } else {
      if (serviceDefinition.one_or_more === 'one' && newService.quantity > 0) {
        newService.quantity = 1;
      }
    }
  }
  return newService;
};

const journeyServicesExceptPriciest = (jobId, serviceDefinitions, getters) => {
  const allJourneysByGroupName = getters
    .allSelectedServices(jobId)
    .map((service) => {
      return {
        ...serviceDefinitions[service.service],
        ...service,
      };
    })
    .filter((service) => {
      return service.journey_to === true && service.group_name.de;
    })
    .reduce(
      (acc, service) => ({
        ...acc,
        [service.group_name.de]: [
          ...(acc[service.group_name.de] || []),
          service,
        ],
      }),
      {},
    );

  return Object.values(allJourneysByGroupName)
    .map((serviceList) => {
      return (<any>serviceList)
        .sort((a, b) => {
          return b.price_purchasing - a.price_purchasing;
        })
        .slice(1);
    })
    .reduce((acc, serviceList) => [...acc, ...serviceList], []);
};

const addLinkKey = (getters, jobId, payload) => {
  if (getters.duplicateServicesAllowed(jobId)) {
    // True means, autogenerate this in the mutation
    payload.service_tag_link_key = true;
  }
};

const addRequiredServices = (
  commit,
  getters,
  serviceDefinitions,
  newService,
  jobId,
  newActiveServices,
) => {
  const requiredBy = new RequiredByRefCount(
    newService.service,
    newService.productId,
  );

  normalizeServiceList(Object.values(serviceDefinitions), newActiveServices)
    .filter((requiredService) => requiredService.service !== newService.service)
    .forEach(
      (requiredService: OrderCreateService & { invoicing_target?: string }) => {
        const requiredServiceDefinition =
          serviceDefinitions[requiredService.service];
        if (requiredServiceDefinition) {
          requiredService.price_lists = requiredServiceDefinition.price_lists;
          requiredService.price_purchasing =
            requiredServiceDefinition.price_purchasing;
          requiredService.price_selling =
            requiredServiceDefinition.price_selling;
          requiredService.invoicing_target =
            requiredServiceDefinition.invoicing_target;
        }
        const serviceIndex = newActiveServices.findIndex(
          (service) => requiredService.service === service.service,
        );
        if (serviceIndex === -1) {
          addLinkKey(getters, jobId, requiredService);
          commit(SERVICE_SELECTED_ADD, {
            jobId: jobId,
            newService: {
              ...requiredService,
              productId: newService.productId,
              requiredBy,
            },
          });
        } else if ((<any>requiredService).productId) {
          commit(SERVICE_SELECTED_SPECIFY, {
            jobId: jobId,
            serviceIndex,
            newService: {
              ...requiredService,
              productId: newService.productId,
              requiredBy,
            },
          });
        } else if (
          serviceDefinitions[newService.service].only_in_combination_with.some(
            (s) => s.service === requiredService.service,
          )
        ) {
          addLinkKey(getters, jobId, requiredService);
          commit(SERVICE_SELECTED_ADD, {
            jobId: jobId,
            newService: {
              ...requiredService,
              quantity: 1,
              productId: newService.productId,
              requiredBy,
            },
          });
        }
      },
    );

  journeyServicesExceptPriciest(jobId, serviceDefinitions, getters).map(
    (service) => {
      commit(SERVICE_SELECTED_ITEM_DELETE, {
        jobId: jobId,
        serviceIndex: getters.getServiceIndex({
          jobId: jobId,
          service: service.service,
          productId: null,
        }),
      });
    },
  );
};

export default {
  ...actionsToMutations([SERVICE_SELECTED_REFRESH]),
  [SERVICE_SELECTED_JOB_ITEMS_ADD]: ({ commit }, payload) => {
    let serviceElement = -1;
    const payload_selected = payload.services.map((service) => {
      serviceElement++;
      return {
        ...service,
        elementIndex: serviceElement,
        jobId: payload.jobId,
        productId: service.product_id,
      };
    });
    commit(SERVICE_SELECTED_JOB_ITEMS_ADD, {
      [payload.jobId]: payload_selected,
    });
  },
  [SERVICE_SELECTED_ITEM_DELETE]: ({ commit, getters, state }, payload) => {
    const serviceDefinitions = getters.serviceDefinitions(payload.jobId);
    const serviceIndex = getters.getServiceIndex({
      jobId: payload.jobId,
      service: payload.service,
      productId: payload.productId,
    });
    commit(SERVICE_SELECTED_ITEM_DELETE, {
      jobId: payload.jobId,
      serviceIndex,
      serviceDefinitions,
    });
  },
  [SERVICE_SELECTED_ITEM_UNREF]: ({ commit, getters }, payload) => {
    const serviceIndex = getters.getServiceIndex({
      jobId: payload.jobId,
      service: payload.service,
      productId: payload.productId,
    });
    if (serviceIndex < 0) {
      return;
    }
    commit(SERVICE_SELECTED_ITEM_UNREF, {
      ...payload,
      serviceIndex,
    });
  },
  [SERVICE_DEFINITIONS_FETCH]: async ({ commit, getters }, payload) => {
    const locale = getters.locale;
    return axios
      .get(
        `/api/${payload.context}/opportunities/${payload.opportunity}/services`,
      )
      .then((response) => {
        if (response.data.length > 0) {
          const serviceDefinitions = response.data.sort((a, b) => {
            const aTitle =
              (payload.useExternalNames
                ? a.description[locale] || a.description.en
                : a.name) || '-none-';
            const bTitle =
              (payload.useExternalNames
                ? b.description[locale] || b.description.en
                : b.name) || '-none-';
            return aTitle.localeCompare(bTitle);
          });
          commit(SERVICE_DEFINITIONS_FETCH, serviceDefinitions);
        }
      });
  },
  [SERVICE_SELECTED_SPECIFY]: ({ commit, getters }, payload) => {
    if (payload.newService.service) {
      const currentActiveServices = getters.allSelectedServices(payload.jobId);
      const serviceDefinitions = getters.serviceDefinitions(payload.jobId);
      const locale = getters.locale;
      const currentActiveService = getters.activeService({
        jobId: payload.jobId,
        productId: payload.newService.productId,
        elementIndex: payload.elementIndex,
      });

      payload.newService = changeServiceOfServiceGroupAndAdjustQuantity(
        serviceDefinitions,
        currentActiveService,
        currentActiveServices,
        payload.newService,
        locale,
      );

      const newActiveServices = currentActiveServices.map((service) => {
        if (!service.service) {
          return {
            ...service,
            ...payload.newService,
          };
        }
        return service;
      });

      addLinkKey(getters, payload.jobId, payload.newService);
      commit(SERVICE_SELECTED_SPECIFY, {
        serviceIndex: getters.getServiceIndex({
          jobId: payload.jobId,
          service: currentActiveService.service,
          productId: currentActiveService.productId,
        }),
        jobId: payload.jobId,
        newService: payload.newService,
        isNew: payload.isNew,
      });

      if (
        payload?.isNew ||
        payload.newService.service != currentActiveService?.service
      ) {
        addRequiredServices(
          commit,
          getters,
          serviceDefinitions,
          payload.newService,
          payload.jobId,
          newActiveServices,
        );
      }
    }
  },
  [SERVICE_SELECTED_ADD]: ({ commit, getters }, payload) => {
    const serviceDefinitions = getters.serviceDefinitions(payload.jobId);

    let newService = {
      jobId: payload.jobId,
      quantity: payload.quantity ?? 1,
      service: null,
      price_lists: payload.price_lists,
      price_purchasing: payload.price_purchasing,
      price_selling: payload.price_selling,
      invoicing_target: payload.invoicing_target,
      value_entry: payload.value_entry ?? 1,
      productId: payload.productId ? payload.productId : null,
      requiredBy: payload.productId
        ? new RequiredByRefCount(payload.productId)
        : null,
    };

    if (payload.serviceSfid) {
      const currentServices = getters.allSelectedServices(payload.jobId);
      const alreadyPresentAtIndex = currentServices.findIndex(
        (svc) => svc.service === payload.serviceSfid,
      );
      if (alreadyPresentAtIndex >= 0) {
        if (serviceDefinitions[payload.serviceSfid]?.one_or_more !== 'one') {
          commit(SERVICE_SELECTED_SPECIFY, {
            serviceIndex: alreadyPresentAtIndex,
            jobId: payload.jobId,
            newService: {
              quantity:
                currentServices[alreadyPresentAtIndex].quantity +
                newService.quantity,
              requiredBy: newService.requiredBy,
              productId: newService.productId,
            },
          });
        }
        const newActiveServices = getters.allSelectedServices(payload.jobId);

        addRequiredServices(
          commit,
          getters,
          serviceDefinitions,
          newService,
          payload.jobId,
          newActiveServices,
        );

        return;
      }

      newService = {
        ...newService,
        service: payload.serviceSfid,
      };
      addLinkKey(getters, payload.jobId, newService);
    }
    commit(SERVICE_SELECTED_ADD, {
      jobId: payload.jobId,
      newService: newService,
    });

    if (newService.service) {
      const newActiveServices = getters.allSelectedServices(payload.jobId);

      addRequiredServices(
        commit,
        getters,
        serviceDefinitions,
        newService,
        payload.jobId,
        newActiveServices,
      );
    }
  },
  [SERVICE_SELECTED_ADD_QUANTITY]({ commit }, payload) {
    return commit(SERVICE_SELECTED_ADD_QUANTITY, payload);
  },
  [SERVICE_CHANGE_QUANTITY]({ commit, getters }, payload) {
    const serviceDefinitions = getters.serviceDefinitions(payload.jobId);
    return commit(SERVICE_CHANGE_QUANTITY, {
      ...payload,
      serviceDefinitions,
    });
  },
  [SERVICE_PRESELECT_AUTOMATION]: ({ commit, getters }, payload) => {
    return commit(SERVICE_PRESELECT_AUTOMATION, payload);
  },
  [SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT]: ({ commit, getters }, payload) => {
    return commit(SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT, payload);
  },
  [CUSTOM_SERVICE_PUSH_ERROR]: ({ commit }, payload) => {
    commit(CUSTOM_SERVICE_PUSH_ERROR, payload);
  },
  [CUSTOM_SERVICE_RESET_ERRORS]: ({ commit }) => {
    commit(CUSTOM_SERVICE_RESET_ERRORS);
  },
};
